import { useEffect, useRef, useState } from "react";
import { rowGapMin, rowGapMax, marqueeSpeed } from "../constants";

// Meet een lijst en beslist wat er moet gebeuren:
//  - past hij in de kaart, dan wordt de ruimte tussen de rijen wat groter
//    zodat een korte lijst de kaart netjes vult (maar nooit meer dan
//    rowGapMax, anders krijg je gaten);
//  - past hij niet, dan rolt de lijst rustig door.
//
// Geeft terug: refs om op de kaart en de lijst te zetten, de ruimte tussen de
// rijen, en hoe lang één ronde doorrollen duurt (0 = niet rollen).
export function useAutoScrollList(items) {
  const viewportRef = useRef(null);
  const listRef = useRef(null);
  const [layout, setLayout] = useState({ gap: rowGapMin, duration: 0 });

  useEffect(() => {
    const viewport = viewportRef.current;
    const list = listRef.current;
    if (!viewport || !list) return undefined;

    const measure = () => {
      const count = list.children.length;
      if (!count) return;

      // Reken terug naar de hoogte van de rijen zónder tussenruimte. Dat
      // getal verandert niet als we de ruimte daarna aanpassen, dus de meting
      // blijft stabiel en gaat niet heen en weer klapperen.
      const appliedGap = parseFloat(getComputedStyle(list).rowGap) || 0;
      const rowsHeight = list.scrollHeight - appliedGap * (count - 1);
      const available = viewport.clientHeight;
      const gaps = Math.max(1, count - 1);
      const tight = rowsHeight + rowGapMin * gaps;

      const tooLong = tight - available > 8;
      const next = tooLong
        ? {
            gap: rowGapMin,
            duration: Math.max(
              14,
              Math.round((tight + rowGapMin) / marqueeSpeed),
            ),
          }
        : {
            gap: Math.min(
              rowGapMax,
              Math.max(rowGapMin, (available - rowsHeight) / gaps),
            ),
            duration: 0,
          };

      // Alleen bijwerken als er echt iets verandert, anders blijft hij
      // zichzelf opnieuw meten.
      setLayout((prev) =>
        Math.abs(prev.gap - next.gap) < 0.5 && prev.duration === next.duration
          ? prev
          : next,
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(list);
    return () => observer.disconnect();
  }, [items]);

  return { viewportRef, listRef, ...layout };
}
